/** In-memory preload cache so carousel images load reliably on slow networks. */
const preloadCache = new Map();
/** URLs that have completed onload at least once. */
const loadedUrlSet = new Set();

/**
 * Preload a single loading image URL. Resolves only after onload.
 * @param {string} src
 * @returns {Promise<string>}
 */
export function preloadLoadingImage(src) {
  if (!src) {
    return Promise.reject(new Error('Missing image src'));
  }

  if (loadedUrlSet.has(src)) {
    return Promise.resolve(src);
  }

  const cached = preloadCache.get(src);
  if (cached) {
    return cached;
  }

  const promise = new Promise((resolve, reject) => {
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => {
      loadedUrlSet.add(src);
      resolve(src);
    };
    img.onerror = () => reject(new Error(`Failed to preload: ${src}`));
    img.src = src;
  });

  preloadCache.set(src, promise);
  return promise;
}

export function clearPreloadEntry(src) {
  preloadCache.delete(src);
}

export function isLoadingImageReady(src) {
  return Boolean(src && loadedUrlSet.has(src));
}

/**
 * Preload multiple images; failures do not block others.
 * @param {string[]} sources
 * @returns {Promise<PromiseSettledResult<string>[]>}
 */
export function preloadLoadingImages(sources = []) {
  return Promise.allSettled(sources.map((src) => preloadLoadingImage(src)));
}

export function isLoadingImageCached(src) {
  return isLoadingImageReady(src);
}

export function areAllLoadingImagesCached(sources = []) {
  return sources.length > 0 && sources.every((src) => isLoadingImageReady(src));
}

/** Add URLs that finished loading into a Set. */
export function seedLoadedSrcsFromPreloadCache(sources, targetSet) {
  sources.forEach((src) => {
    if (isLoadingImageReady(src)) {
      targetSet.add(src);
    }
  });
}

/**
 * Wait for critical carousel images (0 + 1) with a hard cap for slow networks.
 * @param {string} src0
 * @param {string} src1
 * @param {number} maxMs
 */
export function waitForCriticalLoadingImages(src0, src1, maxMs) {
  const tasks = [src0, src1].filter(Boolean).map((src) =>
    preloadLoadingImage(src).catch(() => src)
  );

  if (!tasks.length) {
    return Promise.resolve();
  }

  return Promise.race([
    Promise.all(tasks),
    new Promise((resolve) => {
      window.setTimeout(resolve, maxMs);
    }),
  ]);
}

/**
 * Wait until an image is actually loaded; retries on failure (slow networks).
 * @param {string} src
 * @param {number} [maxAttempts]
 */
export async function ensureLoadingImageLoaded(src, maxAttempts = 8) {
  if (!src) {
    throw new Error('Missing image src');
  }

  if (isLoadingImageReady(src)) {
    return src;
  }

  let lastError;
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    try {
      const loaded = await preloadLoadingImage(src);
      return loaded;
    } catch (error) {
      lastError = error;
      clearPreloadEntry(src);
      await new Promise((resolve) => {
        window.setTimeout(resolve, 400 + attempt * 200);
      });
    }
  }

  throw lastError ?? new Error(`Failed to load: ${src}`);
}
